import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { gridActionSx } from "../widgets/gridActionSx";
import { GET_DIAMS } from "../../backend/graphql";
import {
  GraphQLDataGrid,
  GraphQLGridParameters,
} from "../widgets/GraphQLDataGrid";
import { adminTagsColumn, handleNavigatePrice, renderPricing } from "./Stock";
import { useAppSelector, useHistoryState } from "../../app/hooks";
import { Roles } from "../../app/features/auth/authTypes";
import { Stack } from "@mui/material";
import QuickFilter from "../widgets/QuickFilter";
import PageTitle from "../widgets/PageTitle";

const gridParameters: (
  recent: boolean | undefined,
  noComments: boolean | undefined,
  role: Roles,
) => GraphQLGridParameters = (
  recent: boolean | undefined,
  noComments: boolean | undefined,
  role: Roles,
) => {
  let filter = "mustCheckPrice == true";
  if (recent)
    filter +=
      ' && lastFantasyUpdate <= "' +
      new Date(new Date().getTime() - 90 * 24 * 60 * 60 * 1000).toISOString() +
      '"';
  if (noComments)
    filter += ' && ( pricingRemark == null || pricingRemark == "" )';
  return {
    graphqlQuery: GET_DIAMS,
    graphqlFilter: filter,
    graphqlItemsPath: (data) => data.diamonds,
    rowHeight: 64,
    allRows: true,
    fields: [
      { field: "representationImageID", label: "Image", type: "image" },
      { field: "id", label: "ID", visible: false, width: 300, type: "uuid" },
      { field: "source", label: "Source", flex: 0.5, visible: false },
      { field: "sKU", label: "SKU", width: 80 },
      { field: "carat", label: "Carat", width: 80, type: "number" },
      { field: "cut", label: "Cut", visible: false },
      { field: "shape", label: "Shape", visible: false },
      { field: "clarity", label: "Clarity", width: 80 },
      { field: "color", label: "Color", flex: 1 },
      {
        field: "langermanColor",
        label: "Langerman Color",
        flex: 1,
        visible: false,
      },
      { field: "flat", label: "Flat", type: "number", visible: false },
      { field: "melee", label: "Melee", type: "number", visible: false },
      {
        field: "isGIACertified",
        label: "GIA?",
        flex: 0.5,
        type: "boolean",
        visible: false,
      },
      { field: "tags", label: "Tags", flex: 0.5, visible: false },
      ...adminTagsColumn(role),
      { field: "price", label: "Price", flex: 0.5, type: "number" },
      {
        field: "currentPricePercentile",
        label: "Pricing",
        flex: 0.5,
        type: "number",
        renderCell: (params: any) => renderPricing(params.value),
      },
      { field: "pricingRemark", label: "Remark", flex: 2, visible: true },
      {
        field: "lastFantasyUpdate",
        label: "Last Check",
        type: "dateTimeShort",
        visible: true,
        defaultSort: "asc",
      },
      {
        field: "ignorePricing",
        label: "Ignore Price",
        type: "boolean",
        width: 100,
        visible: false,
      },
      {
        field: "noLongerAvailable",
        label: "Unavailable",
        type: "boolean",
        width: 100,
        visible: false,
      },
    ],
    // Pricing only: the grading has its own alerts.
    maxActions: 1,
    getActions: ({ navigate, row }) => [
      <GridActionsCellItem
        icon={<AttachMoneyIcon />}
        label="Pricing"
        sx={gridActionSx}
        onClick={() => handleNavigatePrice(row, navigate)}
      />,
    ],
  };
};

const PriceAlert = () => {
  const [noRecent, setNoRecent] = useHistoryState("noRecent", true);
  const [noRemarks, setNoRemarks] = useHistoryState("noRemarks", true);
  const role = useAppSelector((state) => state.auth.role);
  const parameters = gridParameters(noRecent, noRemarks, role);
  return (
    <Stack spacing={1} useFlexGap>
      <PageTitle />
      <Stack direction="row" spacing={1} flexWrap="wrap">
        <QuickFilter
          active={noRecent}
          label="No reviewed recently"
          toggle={(active) => setNoRecent(!active)}
        />
        <QuickFilter
          active={noRemarks}
          label="No remarks"
          toggle={(active) => setNoRemarks(!active)}
        />
      </Stack>
      <GraphQLDataGrid parameters={parameters} elevated />
    </Stack>
  );
};

export default PriceAlert;
